import { useCallback, useEffect, useState } from "react";
import { Loader2, Calculator, CheckCircle2, Send, FileDown, Trash2, RotateCcw, PenLine, Users } from "lucide-react";
import { toast } from "sonner";
import { PayslipInputsDialog } from "./PayslipInputsDialog";

const eur = (v) => (v == null ? "—" : Number(v).toLocaleString("fr-FR", { style: "currency", currency: "EUR" }));
const STATUS = {
  draft: { label: "BROUILLON", color: "#f7c935" }, validated: { label: "VALIDÉ", color: "#38bdf8" },
  published: { label: "PUBLIÉ", color: "#22c55e" }, paid: { label: "PAYÉ", color: "#a3e635" },
};
const btn = "inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-bold transition-colors disabled:opacity-50";

function currentPeriod() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export function PayslipsTab({ api, societeId, onChanged }) {
  const [period, setPeriod] = useState(currentPeriod());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/payroll/payslips", { params: { societe_id: societeId, period } });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Chargement impossible");
    } finally {
      setLoading(false);
    }
  }, [api, societeId, period]);
  useEffect(() => { load(); }, [load]);

  const act = async (key, fn, okMsg) => {
    setBusy(key);
    try {
      const r = await fn();
      if (okMsg) toast.success(typeof okMsg === "function" ? okMsg(r.data) : okMsg);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action impossible");
    } finally {
      setBusy("");
    }
  };

  const openPdf = async (p) => {
    try {
      const r = await api.get(`/payroll/payslips/${p.payslip_id}/pdf`, { responseType: "blob" });
      window.open(URL.createObjectURL(new Blob([r.data], { type: "application/pdf" })), "_blank");
    } catch (e) {
      toast.error("PDF indisponible");
    }
  };

  const slips = data?.payslips || [];
  const pending = data?.pending_employees || [];
  const drafts = slips.filter((s) => s.status === "draft").length;
  const validated = slips.filter((s) => s.status === "validated").length;

  return (
    <div className="space-y-4" data-testid="payslips-tab">
      <div className="flex flex-wrap items-center gap-3">
        <input type="month" value={period} onChange={(e) => e.target.value && setPeriod(e.target.value)} data-testid="payslip-period-input"
          className="bg-[#0b0811] border border-white/10 text-white rounded-lg px-3 py-2 text-sm focus:border-[#f7c935] outline-none" />
        <div className="text-sm font-bold text-white capitalize" data-testid="payslip-period-label">{data?.label}</div>
        <div className="flex-1" />
        {pending.length > 0 && (
          <button onClick={() => act("compute", () => api.post("/payroll/payslips/compute", { societe_id: societeId, period }), (d) => `${d.computed} bulletin(s) calculé(s)`)}
            disabled={!!busy} className={`${btn} bg-[#f7c935] text-[#0b0811] hover:bg-[#ffd75a]`} data-testid="compute-all-btn">
            {busy === "compute" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Calculator className="w-3.5 h-3.5" />} Calculer les bulletins ({pending.length})
          </button>
        )}
        {drafts > 0 && (
          <button onClick={() => window.confirm(`Valider ${drafts} bulletin(s) ? Les écritures de paie seront générées.`) && act("validate-all", () => api.post("/payroll/payslips/validate-all", null, { params: { societe_id: societeId, period } }), (d) => `${d.validated} bulletin(s) validé(s)`)}
            disabled={!!busy} className={`${btn} bg-sky-500/15 text-sky-300 hover:bg-sky-500/25`} data-testid="validate-all-btn">
            {busy === "validate-all" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />} Valider tout ({drafts})
          </button>
        )}
        {validated > 0 && (
          <button onClick={() => act("publish-all", () => api.post("/payroll/payslips/publish-all", null, { params: { societe_id: societeId, period } }), (d) => `${d.published} bulletin(s) publié(s) et salariés notifiés`)}
            disabled={!!busy} className={`${btn} bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25`} data-testid="publish-all-btn">
            {busy === "publish-all" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Publier tout ({validated})
          </button>
        )}
      </div>

      {data && slips.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2 text-[11px]" data-testid="payslip-totals">
          {[["Brut", data.totals.gross], ["Net à payer", data.totals.net], ["Coût employeur", data.totals.employer_cost], ["URSSAF", data.totals.urssaf],
            ["Retraite", data.totals.retraite], ["Prévoyance", data.totals.prevoyance], ["Mutuelle", data.totals.mutuelle], ["PAS", data.totals.pas]].map(([k, v]) => (
            <div key={k} className="bg-[#0b0811] border border-white/5 rounded-lg px-2.5 py-2"><div className="text-slate-500">{k}</div><div className="text-white font-bold font-mono">{eur(v)}</div></div>
          ))}
        </div>
      )}

      {loading ? <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-[#f7c935]" /></div> : (
        <>
          {slips.length === 0 && pending.length === 0 && (
            <div className="text-center py-10 text-slate-500 text-sm" data-testid="payslips-empty">
              <Users className="w-8 h-8 mx-auto mb-2 opacity-40" /> Aucun salarié actif sur cette période — ajoutez des dossiers dans l'onglet « Salariés ».
            </div>
          )}
          {slips.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead><tr className="text-left text-slate-500 border-b border-white/10">
                  <th className="py-2 pr-2">Salarié</th><th className="py-2 pr-2 text-right">Brut</th><th className="py-2 pr-2 text-right">Cotis. sal.</th>
                  <th className="py-2 pr-2 text-right">PAS</th><th className="py-2 pr-2 text-right">Net à payer</th><th className="py-2 pr-2 text-right">Coût employeur</th>
                  <th className="py-2 pr-2">Statut</th><th className="py-2 text-right">Actions</th></tr></thead>
                <tbody>
                  {slips.map((p) => {
                    const sm = STATUS[p.status] || STATUS.draft;
                    return (
                      <tr key={p.payslip_id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`payslip-row-${p.payslip_id}`}>
                        <td className="py-2 pr-2"><div className="text-white font-medium">{p.employee?.first_name} {p.employee?.last_name}</div><div className="text-[10px] text-slate-500">{p.employee?.matricule} · {p.employee?.job_title || "—"}</div></td>
                        <td className="py-2 pr-2 text-right font-mono">{eur(p.gross)}</td>
                        <td className="py-2 pr-2 text-right font-mono text-slate-400">{eur(p.employee_contributions)}</td>
                        <td className="py-2 pr-2 text-right font-mono text-slate-400">{eur(p.pas)}</td>
                        <td className="py-2 pr-2 text-right font-mono text-[#f7c935] font-bold" data-testid={`payslip-net-${p.payslip_id}`}>{eur(p.net_to_pay)}</td>
                        <td className="py-2 pr-2 text-right font-mono text-slate-300">{eur(p.employer_cost)}</td>
                        <td className="py-2 pr-2"><span className="text-[9px] font-black px-1.5 py-0.5 rounded" style={{ background: `${sm.color}20`, color: sm.color }} data-testid={`payslip-status-${p.payslip_id}`}>{sm.label}</span>
                          {p.is_final && <span className="ml-1 text-[9px] font-black px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300" title="Solde de tout compte" data-testid={`payslip-final-${p.payslip_id}`}>STC</span>}
                          {p.paid_at && <div className="text-[9px] text-slate-500 mt-0.5">payé le {p.paid_at}</div>}</td>
                        <td className="py-2 text-right whitespace-nowrap">
                          {p.status === "draft" && <>
                            <button onClick={() => setEditing(p)} className={`${btn} text-slate-300 hover:bg-white/10`} data-testid={`payslip-edit-${p.payslip_id}`} title="Saisie du mois"><PenLine className="w-3.5 h-3.5" /> Saisie</button>
                            <button onClick={() => act(p.payslip_id, () => api.post(`/payroll/payslips/${p.payslip_id}/validate`), "Bulletin validé · écritures de paie générées")} disabled={!!busy}
                              className={`${btn} bg-sky-500/15 text-sky-300 hover:bg-sky-500/25 ml-1`} data-testid={`payslip-validate-${p.payslip_id}`}>{busy === p.payslip_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />} Valider</button>
                            <button onClick={() => window.confirm("Supprimer ce brouillon ?") && act(p.payslip_id, () => api.delete(`/payroll/payslips/${p.payslip_id}`), "Brouillon supprimé")} className={`${btn} text-red-400 hover:bg-red-500/10 ml-1`} data-testid={`payslip-delete-${p.payslip_id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                          </>}
                          {p.status !== "draft" && <button onClick={() => openPdf(p)} className={`${btn} text-slate-300 hover:bg-white/10`} data-testid={`payslip-pdf-${p.payslip_id}`}><FileDown className="w-3.5 h-3.5" /> PDF</button>}
                          {p.status === "validated" && <button onClick={() => act(p.payslip_id, () => api.post(`/payroll/payslips/${p.payslip_id}/publish`), "Bulletin publié · salarié notifié")} disabled={!!busy}
                            className={`${btn} bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25 ml-1`} data-testid={`payslip-publish-${p.payslip_id}`}><Send className="w-3.5 h-3.5" /> Publier</button>}
                          {(p.status === "validated" || p.status === "published") && <button onClick={() => window.confirm("Repasser en brouillon ? Les écritures de paie seront supprimées.") && act(p.payslip_id, () => api.post(`/payroll/payslips/${p.payslip_id}/unvalidate`), "Bulletin repassé en brouillon")}
                            className={`${btn} text-slate-400 hover:bg-white/10 ml-1`} data-testid={`payslip-unvalidate-${p.payslip_id}`} title="Repasser en brouillon"><RotateCcw className="w-3.5 h-3.5" /></button>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {pending.length > 0 && (
            <div className="bg-[#0b0811] border border-dashed border-white/10 rounded-lg p-3" data-testid="pending-employees">
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2">Sans bulletin sur la période</div>
              <div className="flex flex-wrap gap-2">
                {pending.map((e) => (
                  <button key={e.employee_id} onClick={() => act(e.employee_id, () => api.post("/payroll/payslips/compute", { societe_id: societeId, period, employee_id: e.employee_id }), "Bulletin calculé")}
                    disabled={!!busy} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-xs text-slate-200" data-testid={`compute-employee-${e.employee_id}`}>
                    {busy === e.employee_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Calculator className="w-3 h-3 text-[#f7c935]" />} {e.first_name} {e.last_name} <span className="text-slate-500">· {eur(e.contract?.gross_monthly)} brut</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </>
      )}
      {editing && <PayslipInputsDialog api={api} payslipId={editing.payslip_id} onClose={(changed) => { setEditing(null); if (changed) { load(); onChanged?.(); } }} />}
    </div>
  );
}
